// src/components/PedidoCard/components/CancelPedido/CancelPedido.tsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import MiniLoadingSpinner from "../../../../../../../../../../components/Spinners/MiniLoadingSpinner";
import RefundManager from "../../../../../../../../Caja/Receipts/components/Table/components/ReceiptDetails/components/RefundManager/RefundManager";
import { getPaymentReceiptById } from "../../../../../../../../../../crud/payments_api/payment_receipts";
import { markPedidoAsCancelledById } from "../../../../../../../../../../crud/pedidos_api/pedidos";
import { getAutoPrintCancellationComandas } from "../../../../../../../../../../crud/localDB/pedidosConfig";
import { Pedido } from "../../../../../../../types";

interface RenderPedidoProps {
  pedido: Pedido;
  setPedidos: React.Dispatch<React.SetStateAction<Pedido[]>>;
  show: boolean;
  setShow: (show: boolean) => void;
}

type Step = "idle" | "loading" | "refunding" | "confirm";

const CancelPedido: React.FC<RenderPedidoProps> = ({
  pedido,
  setPedidos,
  show,
  setShow,
}) => {
  const [step, setStep] = useState<Step>("idle");
  const [receipt, setReceipt] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [printToKitchen, setPrintToKitchen] = useState(false);
  const [autoPrintCancellationComandas, setAutoPrintCancellationComandas] = useState(false);
  const isPayed = pedido.payed;

  useEffect(() => {
    let isMounted = true;

    getAutoPrintCancellationComandas()
      .then((value) => {
        if (!isMounted) return;
        setAutoPrintCancellationComandas(value);
        if (value) {
          setPrintToKitchen(true);
        }
      })
      .catch((error) => {
        console.error("Error loading cancellation comanda config:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Whenever `show` opens, pick the right next step
  useEffect(() => {
    if (!show) {
      setStep("idle");
      return;
    }
    if (!pedido.payed) {
      setStep("confirm");
    } else {
      setStep("loading");
      getPaymentReceiptById(pedido.receipt_id ?? 0)
        .then((resp) => {
          setReceipt(resp.data);
          setStep("refunding");
        })
        .catch((err) => {
          console.error("Error fetching receipt:", err);
          toast.error("No se pudo cargar el recibo");
          setShow(false);
        });
    }
  }, [show, pedido.payed, pedido.receipt_id, setShow]);

  // Once fully refunded (or refund manager closed), move to confirmation
  const handleRefundComplete = () => {
    if (isPayed) {
      // Si el pedido está pagado, cancelar directamente tras refund y cerrar el modal
      setLoading(true);
      markPedidoAsCancelledById(pedido.pedido_id, autoPrintCancellationComandas || printToKitchen)
        .then(() => {
          toast.success("Pedido marcado como anulado");
          setPedidos((prev) =>
            prev.filter((p) => p.pedido_id !== pedido.pedido_id)
          );
          setShow(false);
        })
        .catch((err) => {
          console.error("Error anulando pedido:", err);
          toast.error("No se pudo anular el pedido");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setStep("confirm");
    }
  };

  // Final cancellation API call
  const handlePedidoCancelled = () => {
    setLoading(true);
    markPedidoAsCancelledById(pedido.pedido_id, autoPrintCancellationComandas || printToKitchen)
      .then(() => {
        toast.success("Pedido marcado como anulado");
        setPedidos((prev) =>
          prev.filter((p) => p.pedido_id !== pedido.pedido_id)
        );
        setShow(false);
      })
      .catch((err) => {
        console.error("Error anulando pedido:", err);
        toast.error("No se pudo anular el pedido");
      }).finally(() => {
        setLoading(false);
      }
      );
  };

  // Idle: render nothing
  if (step === "idle") return null;

  // Loading spinner while fetching receipt
  if (step === "loading") {
    return (
      <Modal show centered className="form-modal">
        <MiniLoadingSpinner />
      </Modal>
    );
  }

  // RefundManager step
  if (step === "refunding") {
    return (
      <RefundManager
        receipt={receipt}
        show={true}
        onClose={handleRefundComplete}
        onRefundConfirmed={handleRefundComplete}
      />
    );
  }

  // Confirmation modal (shared by both paid & unpaid)
  if (step === "confirm") {
    return (
      <Modal show onHide={() => setShow(false)} className="form-modal">
        <Modal.Header>
          <Modal.Title>¿Está seguro que desea anular el pedido?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Al anular el pedido se eliminarán todas las comandas asociadas al
            mismo.
          </p>
          {autoPrintCancellationComandas ? (
            <div className="mb-3 text-muted" style={{ paddingTop: '0.5rem', borderTop: '1px solid #dee2e6' }}>
              Se notificará a cocina automáticamente por configuración.
            </div>
          ) : (
            <Form.Group className="mb-3" style={{ paddingTop: '0.5rem', borderTop: '1px solid #dee2e6' }}>
              <Form.Check
                type="switch"
                id="print-to-kitchen-cancel"
                label="Notificar a cocina"
                checked={printToKitchen}
                onChange={(e) => setPrintToKitchen(e.target.checked)}
                disabled={loading}
                style={{ fontWeight: 500 }}
              />
            </Form.Group>
          )}
          {loading ? <MiniLoadingSpinner /> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="none" className="form-button-cancel"  onClick={() => setShow(false)}>
            Cancelar
          </Button>
          <Button variant="none" 
          className="form-button-create" 
          onClick={handlePedidoCancelled}
          disabled={loading}>
            Anular Pedido
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return null;
};

export default CancelPedido;
